/* eslint-disable import/no-cycle */
import {
  WidgetTypeConfig,
  ConfigurationPanelStructureItemEnum,
  ElementPartTypeEnum,
  StepperSignEnum,
  TemplateStructureEnum,
  ComponentNameEnum,
} from '@/types';
import getColorFieldConfig from '@/components/template-builder/config/fields/color-config';
import mediaDisplayFieldConfig from '@/components/template-builder/config/fields/media-display-config';
import getStepperFieldConfig from '@/components/template-builder/config/fields/stepper-config';
import getInputVariablesFieldConfig from '@/components/template-builder/config/fields/input-variables-config';
import getGamePrizesEditorFieldConfig from '@/components/template-builder/config/fields/game-prizes-editor-config';
import getDropdownFieldConfig from '@/components/template-builder/config/fields/dropdown-config';
import getGameTestButtonFieldConfig from '@/components/template-builder/config/fields/game-test-button-config';
import getGameStockEditorFieldConfig from '@/components/template-builder/config/fields/game-stock-editor-config';
import {
  setCssProperty,
  setMediaDisplay,
  setAttribute,
  setGameWheelAttribute,
  setGameCssProperty,
} from '@/components/template-builder/setters';
import {
  getClassByName,
  getCssPropertyByName,
  getAttributeByName,
  getAlwaysTrue,
  getGameCssProperty,
} from '@/components/template-builder/getters';
import getConditionsAndRepetitionGroup from '@/components/template-builder/config/fields/conditions-repetition-group-helper';
// eslint-disable-next-line import/no-cycle
import getWidgetCommonStylesGroup from '@/components/template-builder/config/fields/widget-common-styles-config';

const displayWidgetGameWheelConfigV4: WidgetTypeConfig = {
  tabPanel: {
    label: 'templateBuilder.widgetsHeaders.gameWheel',
    items: [
      {
        type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
        tabItemType: TemplateStructureEnum.GROUP,
        label: 'templateBuilder.configs.gameConfiguration',
        scrollable: true,
        tabPanel: {
          items: [
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gamePrizes',
              selector: 'body #spm_body #{ID}',
              items: [
                {
                  label: '',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-prizes',
                    getter: getAttributeByName,
                    setters: [setAttribute],
                  }],
                  ...getGamePrizesEditorFieldConfig({ showColorPicker: true }),
                },
              ],
            },
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gameSettings',
              selector: 'body #spm_body #{ID}',
              items: [
                {
                  label: 'templateBuilder.fields.gameSpinDuration',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-spin-duration',
                    getter: getAttributeByName,
                    setters: [setAttribute],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.NONE, 500, 1000, 10000),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.NONE, 500, 1000, 10000).options,
                    defaultValue: 4000,
                    suffix: 'ms',
                  },
                },
              ],
            },
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gameStock',
              selector: 'body #spm_body #{ID}',
              items: [
                {
                  label: '',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-prizes',
                    getter: getAttributeByName,
                    setters: [setAttribute],
                  }],
                  ...getGameStockEditorFieldConfig({ gameType: 'wheel' }),
                },
              ],
            },
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gameTest',
              selector: 'body #spm_body #{ID}',
              items: [
                {
                  label: 'templateBuilder.fields.testGame',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'test',
                    getter: getAlwaysTrue,
                    setters: [],
                  }],
                  ...getGameTestButtonFieldConfig('wheel'),
                },
              ],
            },
          ],
        },
      },
      {
        type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
        tabItemType: TemplateStructureEnum.GROUP,
        label: 'templateBuilder.configs.gameWheelStyles',
        tabPanel: {
          items: [
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gameWheelAppearance',
              selector: 'body #spm_body #{ID}',
              canSave: true,
              elementPartType: ElementPartTypeEnum.STYLES,
              items: [
                {
                  label: 'templateBuilder.fields.gameWheelDisplayMode',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-display-mode',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  componentName: ComponentNameEnum.BUTTON_GROUP,
                  cardStyle: true,
                  options: {
                    full: {
                      property: 'data-display-mode',
                      defaultValue: 'full',
                      title: 'templateBuilder.games.wheelDisplayMode.full',
                      svg: '<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="14" fill="currentColor"/></svg>',
                    },
                    top: {
                      property: 'data-display-mode',
                      defaultValue: 'full',
                      title: 'templateBuilder.games.wheelDisplayMode.top',
                      svg: '<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><path d="M6 20a14 14 0 0 1 28 0z" fill="currentColor"/></svg>',
                    },
                    bottom: {
                      property: 'data-display-mode',
                      defaultValue: 'full',
                      title: 'templateBuilder.games.wheelDisplayMode.bottom',
                      svg: '<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><path d="M6 20a14 14 0 0 0 28 0z" fill="currentColor"/></svg>',
                    },
                    left: {
                      property: 'data-display-mode',
                      defaultValue: 'full',
                      title: 'templateBuilder.games.wheelDisplayMode.left',
                      svg: '<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><path d="M20 6a14 14 0 0 0 0 28z" fill="currentColor"/></svg>',
                    },
                    right: {
                      property: 'data-display-mode',
                      defaultValue: 'full',
                      title: 'templateBuilder.games.wheelDisplayMode.right',
                      svg: '<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><path d="M20 6a14 14 0 0 1 0 28z" fill="currentColor"/></svg>',
                    },
                  },
                },
                {
                  label: 'templateBuilder.fields.gameWheelPointerPosition',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-pointer-position',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  componentName: ComponentNameEnum.BUTTON_GROUP,
                  cardStyle: true,
                  options: {
                    top: {
                      property: 'data-pointer-position',
                      defaultValue: 'top',
                      title: 'templateBuilder.games.wheelPointerPosition.top',
                      svg: '<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="12" fill="none" stroke="currentColor" stroke-width="2"/><polygon points="20,3 16,10 24,10" fill="currentColor"/></svg>',
                    },
                    bottom: {
                      property: 'data-pointer-position',
                      defaultValue: 'top',
                      title: 'templateBuilder.games.wheelPointerPosition.bottom',
                      svg: '<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="12" fill="none" stroke="currentColor" stroke-width="2"/><polygon points="20,37 16,30 24,30" fill="currentColor"/></svg>',
                    },
                    left: {
                      property: 'data-pointer-position',
                      defaultValue: 'top',
                      title: 'templateBuilder.games.wheelPointerPosition.left',
                      svg: '<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="12" fill="none" stroke="currentColor" stroke-width="2"/><polygon points="3,20 10,16 10,24" fill="currentColor"/></svg>',
                    },
                    right: {
                      property: 'data-pointer-position',
                      defaultValue: 'top',
                      title: 'templateBuilder.games.wheelPointerPosition.right',
                      svg: '<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="12" fill="none" stroke="currentColor" stroke-width="2"/><polygon points="37,20 30,16 30,24" fill="currentColor"/></svg>',
                    },
                  },
                },
                {
                  label: 'templateBuilder.fields.gameWheelSize',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-wheel-size',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 10, 150, 500),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 10, 150, 500).options,
                    defaultValue: 300,
                  },
                },
                {
                  label: 'templateBuilder.fields.gameWheelBorderColor',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-border-color',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getColorFieldConfig('#1a1a1a', true, false),
                },
                {
                  label: 'templateBuilder.fields.gameWheelBorderWidth',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-border-width',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 20),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 20).options,
                    defaultValue: 8,
                  },
                },
                {
                  label: 'templateBuilder.fields.gameWheelPointerColor',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-pointer-color',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getColorFieldConfig('#1a1a1a', true, false),
                },
                {
                  label: 'templateBuilder.fields.gameWheelTextColor',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-text-color',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getColorFieldConfig('#ffffff', true, false),
                },
                {
                  label: 'templateBuilder.fields.gameWheelFontSize',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-font-size',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 8, 24),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 8, 24).options,
                    defaultValue: 14,
                  },
                },
                {
                  label: 'templateBuilder.fields.gameWheelIconSize',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-icon-size',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 5, 20, 150),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 5, 20, 150).options,
                    defaultValue: 80,
                  },
                },
                {
                  label: 'templateBuilder.fields.gameWheelTextOrientation',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-text-orientation',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getDropdownFieldConfig([
                    { label: 'templateBuilder.fields.gameWheelTextOrientationCurved', value: 'curved' },
                    { label: 'templateBuilder.fields.gameWheelTextOrientationRadial', value: 'radial' },
                    { label: 'templateBuilder.fields.gameWheelTextOrientationHorizontal', value: 'horizontal' },
                  ], 'radial'),
                },
              ],
            },
            getWidgetCommonStylesGroup(),
          ],
        },
      },
      getConditionsAndRepetitionGroup('body #spm_body #{ID}'),
    ],
  },
};

export default displayWidgetGameWheelConfigV4;
